import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Upload, Send, RotateCcw, Check, Trash2, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getFlyerForRegion } from "@/lib/conferencePublic";
import { invalidateConferenceHubCache } from "@/lib/conferencePrefetch";

interface Props {
  region: string;
  /** Optional override – when not provided, defaults to the regional flyer URL. */
  initialFlyerUrl?: string;
  /** Notifies parent so it can refresh `conference_kit_events` after publish. */
  onPublished?: (publicUrl: string) => void;
  regionId?: string | null;
  eventId?: string | null;
}

interface ChatTurn {
  role: "user" | "ai";
  prompt?: string;
  imageUrl?: string;
  attachments?: string[];
}

const slugify = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const PUBLIC_ASSET_ORIGINS = [
  "https://www.ofuesi.org",
  "https://ofuesi.org",
  "https://ofuesi-website.lovable.app",
];

const isPreviewHost = (hostname: string) =>
  /lovableproject\.com$|\.lovable\.app$|\.lovable\.dev$/i.test(hostname) && !/ofuesi(?:-website)?\.lovable\.app$/i.test(hostname);

const toAbsoluteUrl = (value: string) => {
  if (!value) return value;
  if (/^(data:|https?:\/\/)/i.test(value)) return value;
  if (typeof window === "undefined") return value;
  return new URL(value, window.location.origin).toString();
};

const buildImageCandidates = (value: string) => {
  if (!value || /^data:/i.test(value)) return [value];

  const candidates: string[] = [];
  const push = (url?: string | null) => {
    if (!url || candidates.includes(url)) return;
    candidates.push(url);
  };

  if (/^https?:\/\//i.test(value)) {
    push(value);
    try {
      const parsed = new URL(value);
      if (parsed.pathname.startsWith("/flyers/") && isPreviewHost(parsed.hostname)) {
        for (const origin of PUBLIC_ASSET_ORIGINS) {
          push(new URL(parsed.pathname, origin).toString());
        }
      }
    } catch {
      // no-op
    }
    return candidates;
  }

  const relative = value.startsWith("/") ? value : `/${value}`;
  push(toAbsoluteUrl(relative));
  if (relative.startsWith("/flyers/")) {
    for (const origin of PUBLIC_ASSET_ORIGINS) {
      push(new URL(relative, origin).toString());
    }
  }
  return candidates;
};

const fetchImageAsDataUrl = async (value: string): Promise<string> => {
  if (!value) throw new Error("Missing flyer image");
  if (/^data:/i.test(value)) return value;

  let lastError: Error | null = null;

  for (const candidate of buildImageCandidates(value)) {
    try {
      const response = await fetch(candidate, { credentials: "same-origin", redirect: "follow" });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok) {
        throw new Error(`Unable to read flyer image (${response.status})`);
      }
      if (!contentType.startsWith("image/")) {
        throw new Error("Flyer URL did not return an image");
      }

      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Unable to encode flyer image"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
    }
  }

  throw lastError || new Error("Unable to read flyer image");
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const MODEL_OPTIONS = [
  { value: "google/gemini-3.1-flash-image-preview", label: "Nano Banana 2 (Gemini 3.1 Flash Image) — fast & precise" },
  { value: "google/gemini-2.5-flash-image", label: "Nano Banana (Gemini 2.5 Flash Image) — balanced" },
  { value: "google/gemini-3-pro-image-preview", label: "Gemini 3 Pro Image — highest quality (slower)" },
];

export const AIFlyerEditor: React.FC<Props> = ({ region, initialFlyerUrl, onPublished, regionId, eventId }) => {
  const defaultUrl = initialFlyerUrl || getFlyerForRegion(region);
  const [baseUrl, setBaseUrl] = useState<string>(defaultUrl);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [history, setHistory] = useState<ChatTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [model, setModel] = useState<string>(MODEL_OPTIONS[0].value);
  const [mode, setMode] = useState<"ai" | "upload">("upload");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setBaseUrl(initialFlyerUrl || getFlyerForRegion(region));
    setPreviewUrl(null);
    setHistory([]);
    setPrompt("");
    setAttachments([]);
    setUploadFile(null);
    setUploadPreview(null);
  }, [region, initialFlyerUrl]);

  useEffect(() => {
    let cancelled = false;

    const loadLatestPublishedFlyer = async () => {
      if (!regionId) return;

      const [{ data: eventRow }, { data: conferenceRow }, { data: contentRow }] = await Promise.all([
        eventId
          ? (supabase.from("conference_kit_events" as any) as any)
              .select("flyer_url")
              .eq("id", eventId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from("conferences")
          .select("flyer_url")
          .eq("region_id", regionId)
          .eq("is_active", true)
          .eq("status", "upcoming")
          .ilike("conference_name", "%2026%")
          .limit(1)
          .maybeSingle(),
        (supabase.from("conference_content" as any) as any)
          .select("data")
          .eq("region_id", regionId)
          .eq("section", "flyer")
          .eq("conference_slug", "regional-conference-2026")
          .eq("is_active", true)
          .limit(1)
          .maybeSingle(),
      ]);

      const savedFlyerUrl =
        (contentRow as any)?.data?.image_url ||
        (contentRow as any)?.data?.url ||
        (conferenceRow as any)?.flyer_url ||
        (eventRow as any)?.flyer_url ||
        initialFlyerUrl ||
        getFlyerForRegion(region);

      if (!cancelled && savedFlyerUrl) {
        setBaseUrl(savedFlyerUrl);
      }
    };

    void loadLatestPublishedFlyer();
    return () => {
      cancelled = true;
    };
  }, [eventId, initialFlyerUrl, region, regionId]);

  const onPickFiles = async (files: FileList | null) => {
    if (!files) return;
    const arr: string[] = [];
    for (const f of Array.from(files).slice(0, 4)) {
      try { arr.push(await readFileAsDataUrl(f)); } catch (_) {}
    }
    setAttachments((prev) => [...prev, ...arr].slice(0, 4));
  };

  const submit = async () => {
    if (!prompt.trim()) {
      toast({ title: "Add a prompt", description: "Describe the change you want.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const userTurn: ChatTurn = { role: "user", prompt, attachments: [...attachments] };
    setHistory((h) => [...h, userTurn]);
    try {
      const sourceImage = await fetchImageAsDataUrl(previewUrl || baseUrl);
      const { data, error } = await supabase.functions.invoke("ai-edit-flyer", {
        body: {
          region,
          currentFlyerUrl: sourceImage,
          prompt,
          attachments,
          model,
        },
      });
      if (error) {
        let message = error.message || "AI edit failed";
        const context = (error as any)?.context;
        if (context?.json) {
          const payload = await context.json().catch(() => null);
          message = payload?.details || payload?.error || message;
        }
        throw new Error(message);
      }
      const imageUrl = (data as any)?.imageUrl;
      if (!imageUrl) throw new Error("No image returned");
      setPreviewUrl(imageUrl);
      setHistory((h) => [...h, { role: "ai", imageUrl }]);
      setPrompt("");
      setAttachments([]);
      toast({ title: "Edit applied", description: "Preview below — keep refining or publish." });
    } catch (e: any) {
      toast({ title: "AI edit failed", description: e.message || String(e), variant: "destructive" });
      // remove the user turn if it failed catastrophically? keep for context.
    } finally {
      setLoading(false);
    }
  };

  const discard = () => {
    setPreviewUrl(null);
    setHistory([]);
    setPrompt("");
    setAttachments([]);
  };

  const downloadPreview = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `flyer-${slugify(region)}-ai-edit.png`;
    a.click();
  };

  // Shared publish flow — writes blob to storage and points conference + content rows at it.
  const publishBlob = async (blob: Blob, contentType: string, suffix: string) => {
    const regionSlug = slugify(region);
    const ts = Date.now();
    const ext = contentType.includes("jpeg") ? "jpg" : contentType.includes("webp") ? "webp" : "png";
    const path = `published-flyers/${regionSlug}/${ts}-${suffix}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("conference-kit-photos")
      .upload(path, blob, { contentType, upsert: true });
    if (upErr) throw upErr;
    const { data: pub } = supabase.storage.from("conference-kit-photos").getPublicUrl(path);
    const publicUrl = pub.publicUrl;

    const resolvedRegionId = regionId || (() => { throw new Error(`Region "${region}" is not linked yet`); })();

    const { error: cErr } = await supabase
      .from("conferences")
      .update({ flyer_url: publicUrl })
      .eq("region_id", resolvedRegionId)
      .eq("is_active", true)
      .eq("status", "upcoming")
      .ilike("conference_name", "%2026%");
    if (cErr) throw cErr;

    const { data: existing, error: existingErr } = await (supabase.from("conference_content" as any) as any)
      .select("id")
      .eq("region_id", resolvedRegionId)
      .eq("section", "flyer")
      .eq("conference_slug", "regional-conference-2026")
      .maybeSingle();
    if (existingErr) throw existingErr;
    if (existing?.id) {
      const { error: contentUpdateErr } = await (supabase.from("conference_content" as any) as any)
        .update({
          data: { image_url: publicUrl, url: publicUrl },
          title: `${region} Conference Flyer`,
          is_active: true,
        })
        .eq("id", existing.id);
      if (contentUpdateErr) throw contentUpdateErr;
    } else {
      const { error: contentInsertErr } = await (supabase.from("conference_content" as any) as any).insert({
        section: "flyer",
        region_id: resolvedRegionId,
        title: `${region} Conference Flyer`,
        data: { image_url: publicUrl, url: publicUrl },
        is_active: true,
        conference_slug: "regional-conference-2026",
      });
      if (contentInsertErr) throw contentInsertErr;
    }

    // Also mirror onto the current kit event so admin views immediately use the new image.
    const kitUpdate = (supabase.from("conference_kit_events" as any) as any)
      .update({ flyer_url: publicUrl });
    const { error: kitErr } = await (eventId ? kitUpdate.eq("id", eventId) : kitUpdate.eq("region_id", resolvedRegionId));
    if (kitErr) throw kitErr;

    // Bust the public Conference Hub prefetch cache so visitors see the new flyer right away.
    try { invalidateConferenceHubCache(); } catch { /* no-op */ }

    onPublished?.(publicUrl);
    return publicUrl;
  };

  // Publish AI-edited flyer
  const publish = async () => {
    if (!previewUrl) return;
    setPublishing(true);
    try {
      const blob = await (await fetch(previewUrl)).blob();
      const publicUrl = await publishBlob(blob, "image/png", "ai");
      toast({ title: "Flyer published", description: `Pushed to ${region} conference & registration pages.` });
      setBaseUrl(publicUrl);
      setPreviewUrl(null);
      setHistory([]);
    } catch (e: any) {
      toast({ title: "Publish failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  // Publish a manually uploaded flyer (no AI involved)
  const publishUploaded = async () => {
    if (!uploadFile) {
      toast({ title: "Pick a flyer image", description: "Choose a PNG/JPG to upload.", variant: "destructive" });
      return;
    }
    setPublishing(true);
    try {
      const publicUrl = await publishBlob(uploadFile, uploadFile.type || "image/png", "upload");
      toast({ title: "Flyer published", description: `Uploaded flyer is live on ${region} pages.` });
      setBaseUrl(publicUrl);
      setUploadFile(null);
      setUploadPreview(null);
    } catch (e: any) {
      toast({ title: "Publish failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  const onPickUpload = async (file: File | null) => {
    setUploadFile(file);
    if (!file) { setUploadPreview(null); return; }
    try { setUploadPreview(await readFileAsDataUrl(file)); } catch { setUploadPreview(null); }
  };

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-amber-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-600" />
          Flyer Editor — {region}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Upload a finished flyer directly, or use the AI editor to refine the current one. Either option publishes live to the {region} conference & registration pages.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode tabs — Manual Upload first as primary option */}
        <div className="flex gap-1 rounded-md bg-white/70 border p-1 text-xs w-fit">
          <button
            onClick={() => setMode("upload")}
            className={`px-3 py-1 rounded ${mode === "upload" ? "bg-green-600 text-white" : "text-muted-foreground"}`}
          >Manual Upload & Publish</button>
          <button
            onClick={() => setMode("ai")}
            className={`px-3 py-1 rounded ${mode === "ai" ? "bg-purple-600 text-white" : "text-muted-foreground"}`}
          >AI Flyer Editor</button>
        </div>

        {mode === "ai" ? (
          <>
            {/* Side-by-side preview */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase text-muted-foreground mb-1">Current</div>
                <img src={baseUrl} alt={`${region} current flyer`} className="w-full rounded-md border shadow-sm" />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase text-muted-foreground mb-1">
                  AI Preview {previewUrl ? "" : "(none yet)"}
                </div>
                {previewUrl ? (
                  <img src={previewUrl} alt="AI preview" className="w-full rounded-md border shadow-sm" />
                ) : (
                  <div className="w-full aspect-[3/4] rounded-md border border-dashed bg-white/60 flex items-center justify-center text-xs text-muted-foreground">
                    Submit a prompt to generate an edit
                  </div>
                )}
              </div>
            </div>

            {/* Chat history */}
            {history.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-md border bg-white/70 p-2 space-y-2 text-xs">
                {history.map((t, i) => (
                  <div key={i} className={t.role === "user" ? "text-foreground" : "text-purple-700"}>
                    <span className="font-semibold mr-1">{t.role === "user" ? "You:" : "AI:"}</span>
                    {t.role === "user" ? (
                      <>
                        {t.prompt}
                        {t.attachments && t.attachments.length > 0 && (
                          <span className="ml-2 text-muted-foreground">({t.attachments.length} attachment{t.attachments.length > 1 ? "s" : ""})</span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">Generated an edited flyer ↑</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Attachments preview */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="relative">
                    <img src={a} className="h-16 w-16 object-cover rounded border" />
                    <button
                      onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-[10px] flex items-center justify-center"
                      title="Remove"
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            {/* Model picker */}
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold uppercase text-muted-foreground">Generative AI Model</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={loading}
                className="text-xs border rounded-md px-2 py-1.5 bg-white"
              >
                {MODEL_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground">
                Try a different model if results don't follow your prompt. Gemini 3 Pro Image gives the best text fidelity for dates/names; Nano Banana 2 is fastest.
              </p>
            </div>

            {/* Prompt input */}
            <div className="space-y-2">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder='Write a literal prompt, e.g. "Replace the date July 11 with July 10th to 11th, 2026" or "Swap the speaker photo with the attached headshot and rename to Pastor John Doe".'
                rows={3}
                disabled={loading}
              />
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => { onPickFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
                />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={loading}>
                  <Upload className="w-4 h-4 mr-1" /> Attach image{attachments.length ? ` (${attachments.length}/4)` : ""}
                </Button>
                <Button size="sm" onClick={submit} disabled={loading || !prompt.trim()}>
                  {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  {loading ? "Editing…" : (previewUrl ? "Refine" : "Generate edit")}
                </Button>
                {previewUrl && (
                  <>
                    <Button size="sm" variant="outline" onClick={downloadPreview} disabled={loading || publishing}>
                      <ImageIcon className="w-4 h-4 mr-1" /> Download preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={discard} disabled={loading || publishing}>
                      <Trash2 className="w-4 h-4 mr-1" /> Discard
                    </Button>
                    <Button size="sm" onClick={publish} disabled={publishing || loading} className="bg-green-600 hover:bg-green-700 text-white">
                      {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                      Publish to {region}
                    </Button>
                  </>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Each prompt edits the latest preview, so you can iteratively refine. Click Discard to start over from the current published flyer.
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Manual upload mode — fallback path when AI editor is not cooperating */}
            <div className="rounded-md border border-green-200 bg-green-50/70 p-3 text-xs text-green-900">
              Skip AI entirely — upload a finished flyer (PNG/JPG/WebP) and publish it directly to the {region} conference & registration pages.
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase text-muted-foreground mb-1">Current Published</div>
                <img src={baseUrl} alt={`${region} current flyer`} className="w-full rounded-md border shadow-sm" />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase text-muted-foreground mb-1">Your Upload</div>
                {uploadPreview ? (
                  <img src={uploadPreview} alt="Upload preview" className="w-full rounded-md border shadow-sm" />
                ) : (
                  <div className="w-full aspect-[3/4] rounded-md border border-dashed bg-white/60 flex items-center justify-center text-xs text-muted-foreground">
                    Choose a flyer image to preview
                  </div>
                )}
              </div>
            </div>
            <input
              ref={uploadRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => { onPickUpload(e.target.files?.[0] || null); if (uploadRef.current) uploadRef.current.value = ""; }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => uploadRef.current?.click()} disabled={publishing}>
                <Upload className="w-4 h-4 mr-1" /> {uploadFile ? "Replace file" : "Choose flyer image"}
              </Button>
              {uploadFile && (
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">{uploadFile.name}</span>
              )}
              <Button
                size="sm"
                onClick={publishUploaded}
                disabled={!uploadFile || publishing}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Publish to {region}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              The uploaded image replaces the flyer on the public {region} conference page and registration page immediately.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AIFlyerEditor;
